import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import { X } from 'lucide-react'
import { fetchStaatidTrail, formatDager, UNKNOWN_ERROR } from './api'
import type { StaatidTrailResponse } from './api'

interface Props {
  /** Roundtrip to show the trail for; null hides the panel. */
  sluttSvvId: number | null
  onClose: () => void
}

// Translation keys, resolved at render (#714).
const CLASS_KEYS = {
  PERSON_TO_BILBRANSJE: 'klasse.PERSON_TO_BILBRANSJE',
  LEASING_TO_BILBRANSJE: 'klasse.LEASING_TO_BILBRANSJE',
  FIRMA_TO_BILBRANSJE: 'klasse.FIRMA_TO_BILBRANSJE',
  BILUTLEIE_TO_BILBRANSJE: 'klasse.BILUTLEIE_TO_BILBRANSJE',
  FORSIKRING_TO_BILBRANSJE: 'klasse.FORSIKRING_TO_BILBRANSJE',
  BILBRANSJE_TO_BILBRANSJE: 'klasse.BILBRANSJE_TO_BILBRANSJE',
  BILBRANSJE_TO_PERSON: 'klasse.BILBRANSJE_TO_PERSON',
  BILBRANSJE_TO_FIRMA: 'klasse.BILBRANSJE_TO_FIRMA',
  BILBRANSJE_TO_LEASING: 'klasse.BILBRANSJE_TO_LEASING',
  BILBRANSJE_TO_BILUTLEIE: 'klasse.BILBRANSJE_TO_BILUTLEIE',
} as const

/**
 * Leg-by-leg drilldown for one roundtrip (issue #274, Fase 3): who held the
 * car, for how long, at each hop. Opened by clicking a scatter point.
 */
export default function TrailPanel({ sluttSvvId, onClose }: Props) {
  const { t } = useTranslation('staatid')
  const [trail, setTrail] = useState<StaatidTrailResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLatest(alive => {
    if (sluttSvvId === null) return
    setLoading(true)
    setError(null)
    setTrail(null)
    fetchStaatidTrail(sluttSvvId)
      .then(r => { if (alive()) setTrail(r.data) })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : UNKNOWN_ERROR) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [sluttSvvId])

  if (sluttSvvId === null) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        data-testid="staatid-trail-panel"
        className="card w-full max-w-2xl max-h-[80vh] overflow-y-auto p-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {trail ? `${trail.merke ?? '—'} ${trail.modell ?? ''}`.trim() : t('trail.fallbackTitle')}
          </h3>
          <button
            data-testid="staatid-trail-close"
            onClick={onClose}
            aria-label={t('trail.close')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div data-testid="staatid-trail-loading" className="animate-pulse h-24 bg-gray-100 dark:bg-dark-border rounded-sm" />
        )}
        {error && <p className="text-sm text-red-600 dark:text-red-400">{t('common.loadError', { error: error === UNKNOWN_ERROR ? t('common.unknownError') : error })}</p>}

        {trail && !loading && (
          <div className="space-y-3">
            {!trail.paired && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('trail.unpaired')}
              </p>
            )}
            <ol className="space-y-2">
              {trail.legs.map((leg, i) => (
                <li
                  key={leg.svv_id}
                  data-testid={`staatid-trail-leg-${i}`}
                  className="border border-gray-200 dark:border-dark-border rounded-sm p-2 text-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                      {leg.seller_org_navn ?? leg.seller_org_nr ?? t('trail.unknown')}
                      {' → '}
                      {leg.buyer_org_navn ?? leg.buyer_org_nr ?? t('trail.sluttkunde')}
                    </span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {new Date(leg.hendelse_tidspunkt).toLocaleDateString('nb-NO')}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {(leg.class && leg.class in CLASS_KEYS
                      ? t(CLASS_KEYS[leg.class as keyof typeof CLASS_KEYS])
                      : leg.class) ?? '—'}
                    {leg.seller_postnummer && ` · ${leg.seller_postnummer}`}
                    {leg.buyer_postnummer && ` → ${leg.buyer_postnummer}`}
                  </div>
                  {leg.dager_til_neste !== null && (
                    <div className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                      {t('trail.daysToNext', { n: formatDager(leg.dager_til_neste) })}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  )
}
